import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Tag, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { getPendingSubscriptions } from '../../services/productSubscriptionService';
import { showLoader, hideLoader } from '../../utils/loader';
import ReviewSubscriptionModal from './ReviewSubscriptionModal';

const SubscriptionRequests = ({ merchantId }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const fetchRequests = useCallback(async (page = 1, size = 10) => {
    setLoading(true);
    showLoader();
    try {
      const fetchPage = merchantId ? 0 : page - 1;
      const fetchSize = merchantId ? 1000 : size;
      const response = await getPendingSubscriptions(fetchPage, fetchSize, merchantId);
      
      if (response.status) {
        let content = response.data.content || [];
        let total = response.data.totalElements || 0;

        if (merchantId) {
          content = content.filter(req => String(req.merchantId) === String(merchantId));
          total = content.length;
          
          const startIndex = (page - 1) * size;
          content = content.slice(startIndex, startIndex + size);
        }

        setRequests(content);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize: size,
          total: total,
        }));
      } else {
        message.error(response.message || 'Failed to fetch pending requests');
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      message.error('An error occurred while fetching requests');
    } finally {
      hideLoader();
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]); // Added merchantId to dependency array

  const handleTableChange = (pag) => {
    fetchRequests(pag.current, pag.pageSize);
  };

  const handleReview = (record) => {
    setSelectedRequest(record);
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: 'Merchant Name',
      dataIndex: 'merchantName',
      key: 'merchantName',
      render: (text) => <span style={{ fontWeight: 600 }}>{text || 'N/A'}</span>,
    },
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'Request Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const s = (status || 'PENDING').toUpperCase();
        const color = s === 'PENDING' ? 'orange' : s === 'APPROVED' ? 'green' : 'red';
        return (
          <Tag color={color} style={{ borderRadius: 20, padding: '2px 12px' }}>
            {s}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const isPending = (record.status || 'PENDING').toUpperCase() === 'PENDING';
        return (
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handleReview(record)}
            style={{
              background: isPending ? '#ff6700' : '#52c41a',
              borderColor: isPending ? '#ff6700' : '#52c41a'
            }}
          >
            {isPending ? 'Review' : 'View'}
          </Button>
        );
      },
    },
  ].filter(col => merchantId ? col.key !== 'merchantName' : true);


  return (
    <div className="subscription-requests-wrapper">
      <div className="product-table-card">
        <Table
          columns={columns}
          dataSource={requests}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
          className="product-table"
        />
      </div>

      <ReviewSubscriptionModal
        visible={isModalVisible}
        request={selectedRequest}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={() => {
          setIsModalVisible(false);
          fetchRequests();
        }}
      />
    </div>
  );
};

export default SubscriptionRequests;
